import { ArrowLeft, ChevronRight } from "lucide-react";
import { useLayoutEffect, useMemo, useState, type ReactNode } from "react";
import { scrollToAppTop } from "./scroll";

export type SettingsStatusTone = "neutral" | "success" | "warning";

export type SettingsNavigatorSection = {
  id: string;
  group: string;
  icon: ReactNode;
  title: string;
  description: string;
  status?: string;
  statusTone?: SettingsStatusTone;
  content?: ReactNode;
  onOpen?: () => void;
};

export function SettingsNavigator({
  eyebrow = "Votre espace",
  title = "Réglages",
  description,
  identity,
  sections,
  footer,
}: {
  eyebrow?: string;
  title?: string;
  description: string;
  identity?: { name: string; detail: string; badge?: string };
  sections: SettingsNavigatorSection[];
  footer?: ReactNode;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeSection = sections.find((section) => section.id === activeId && section.content) ?? null;
  const groups = useMemo(() => {
    const grouped = new Map<string, SettingsNavigatorSection[]>();
    for (const section of sections) grouped.set(section.group, [...(grouped.get(section.group) ?? []), section]);
    return [...grouped.entries()];
  }, [sections]);
  useLayoutEffect(() => { scrollToAppTop(); }, [activeId]);

  if (activeSection) {
    return (
      <div className="ensemble-settings-detail">
        <button type="button" className="ensemble-settings-back" onClick={() => setActiveId(null)}>
          <ArrowLeft aria-hidden="true" />
          Tous les réglages
        </button>
        <header className="ensemble-settings-detail-header">
          <span className="ensemble-settings-detail-icon">{activeSection.icon}</span>
          <div>
            <p className="ensemble-settings-eyebrow">{activeSection.group}</p>
            <h1>{activeSection.title}</h1>
            <p>{activeSection.description}</p>
          </div>
        </header>
        <div className="ensemble-settings-detail-content">{activeSection.content}</div>
      </div>
    );
  }

  return (
    <div className="ensemble-settings-home">
      <header className="ensemble-settings-header">
        <p className="ensemble-settings-eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </header>
      {identity && (
        <section className="ensemble-settings-identity" aria-label="Compte actif">
          <span>{identity.name.slice(0, 1).toUpperCase()}</span>
          <div><strong>{identity.name}</strong><small>{identity.detail}</small></div>
          {identity.badge && <em>{identity.badge}</em>}
        </section>
      )}
      <div className="ensemble-settings-groups">
        {groups.map(([group, items]) => (
          <section className="ensemble-settings-group" key={group}>
            <h2>{group}</h2>
            <div>
              {items.map((section) => (
                <button
                  type="button"
                  className="ensemble-settings-link"
                  key={section.id}
                  disabled={!section.content && !section.onOpen}
                  onClick={() => section.onOpen ? section.onOpen() : setActiveId(section.id)}
                >
                  <span className="ensemble-settings-link-icon">{section.icon}</span>
                  <span className="ensemble-settings-link-copy"><strong>{section.title}</strong><small>{section.description}</small></span>
                  {section.status && <span className={`ensemble-settings-status ${section.statusTone ?? "neutral"}`}>{section.status}</span>}
                  <ChevronRight className="ensemble-settings-chevron" aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
      {footer && <div className="ensemble-settings-footer">{footer}</div>}
    </div>
  );
}
